import { fabric } from 'fabric';
import CreationService from 'services/CreationService';
import { downloadBlob, extractBase64FromUrl, updateCFBucketUrls } from 'utils';

/**
 * Downloads a creation by generating a base64 URL for each slide, converting it to a blob,
 * and then triggering a download of the blob. The base64 URL is generated by creating a canvas,
 * loading the slide content onto the canvas, and then calling `toDataURL` on the canvas.
 *
 * @param {Object} creation - The creation object containing the details of the creation.
 * @param {Function} setLoadingDownload - Function to set the loading state for the download operation.
 * @param {Function} showErrorMessage - Function to display an error message.
 * @returns {Promise<void>} A Promise that resolves when the download operation is complete.
 */
export async function downloadCreation(
  creation,
  setLoadingDownload,
  showErrorMessage,
  shouldDownload = true,
) {
  setLoadingDownload(true);

  const {
    data: { slides },
  } = await CreationService.findOne(creation.id);

  for (let slide of slides) {
    const slideData = await CreationService.getSlideContent(creation.id, slide.id);
    let base64Url;

    await new Promise((resolve) => {
      const tempCanvas = new fabric.Canvas();
      tempCanvas.setWidth(creation.width);
      tempCanvas.setHeight(creation.height);
      const oldWorkspaceCanvasSize = 1500;

      tempCanvas.loadFromJSON(updateCFBucketUrls(slideData.data.content), async () => {
        try {
          base64Url = tempCanvas.toDataURL({
            format: 'png',
            width: creation.width,
            left: (oldWorkspaceCanvasSize - creation.width) / 2,
            top: (oldWorkspaceCanvasSize - creation.height) / 2,
            multiplier: 300 / 300,
          });
        } catch (error) {
          showErrorMessage();
          setLoadingDownload(false);
          console.error('Error downloading creation:', error);
        }

        const { data: blob } = await CreationService.downloadSlide(creation.id, slide.id, {
          content: extractBase64FromUrl(base64Url),
          format: 'png',
          dpi: 300,
          thumbnail: false,
          grayscale: false,
          email: false,
          cmyk: false,
          legacyCreation: true,
        });

        if (shouldDownload) {
          downloadBlob(blob, `${creation.name} - slide ${slide.id}.png`);
        }

        resolve();

        setLoadingDownload(false);
      });
    });
  }
}

/**
 * Converts a creation to a new workspace. This function fetches the creation using its ID
 * and calls the `convertCreationToNewWorkspace` function from the `CreationService` with the creation ID.
 * If the conversion is successful, it redirects the user to the new workspace using the returned `convertedCreationId`.
 *
 * @param {Object} creation - The creation object containing the details of the creation.
 * @param {Function} setLoadingConvert - Function to set the loading state for the conversion operation.
 * @param {Function} showErrorMessage - Function to display an error message.
 * @param {Object} router - The Next.js router object.
 * @returns {Promise<void>} A Promise that resolves when the conversion operation is complete.
 */
export async function convertCreation(creation, setLoadingConvert, showErrorMessage, router) {
  setLoadingConvert(true);

  try {
    /** The downloadCreation function is called with the shouldDownload parameter set to false.*/
    await downloadCreation(creation, setLoadingConvert, showErrorMessage, false);

    const {
      data: { convertedCreationId },
    } = await CreationService.convertCreationToNewWorkspace(creation.id);
    if (!convertedCreationId) throw new Error('Failed to convert creation');

    setLoadingConvert(false);
    router.push(`/workspace/${convertedCreationId}`);
    return;
  } catch (error) {
    showErrorMessage();
    setLoadingConvert(false);
    console.error('Error converting creation:', error);
  }
}
